import { makeProjection } from "@/editor/mercator";
import { obstacleShape, rectToLL } from "@/lib/geo";
import { moduleGroups, rowGaps, sectionSpan } from "@/lib/reportSection";
import { placeLabels } from "@/lib/labels";

import { tr } from "@/lib/i18n";
const API = process.env.REACT_APP_BACKEND_URL;

// similitudine da 2 punti: pixel della foto → mondo del disegno (px mercatore); null se non georeferenziata
export function georefTransform(bg, llToWorld) {
  if (!bg?.px || bg.ll?.length !== 2) return null;
  const [w1, w2] = bg.ll.map(llToWorld), [p1, p2] = bg.px;
  const dw = [w2[0] - w1[0], w2[1] - w1[1]], dp = [p2[0] - p1[0], p2[1] - p1[1]];
  const s = Math.hypot(...dw) / (Math.hypot(...dp) || 1);
  const rot = Math.atan2(dw[1], dw[0]) - Math.atan2(dp[1], dp[0]);
  const c = Math.cos(rot), si = Math.sin(rot);
  return { s, rot, x: w1[0] - s * (c * p1[0] - si * p1[1]), y: w1[1] - s * (si * p1[0] + c * p1[1]) };
}

const loadImg = (src) => new Promise((ok, ko) => {
  const im = new window.Image();
  im.crossOrigin = "anonymous";
  im.onload = () => ok(im);
  im.onerror = () => ko(new Error(tr("immagine non disponibile: {0}", [src])));
  im.src = src;
});

export const COLORS = { roof: "#38bdf8", module: "#1e3a8a", rail: "#0f172a", hook: "#f97316", obstacle: "#ef4444", ballast: "#facc15" };

// immagine ad alta risoluzione della pianta: sfondo (foto georeferenziata o mappa satellitare) + falde, moduli, binari, ganci, ostacoli
export async function renderPlanImage(p, layout, drawing, aspect, opts = {}, maxPx = 3200) {
  const prj = makeProjection(p.lat, p.lng);
  const toW = (ll) => prj.mToWorld(prj.toM(ll[0], ll[1]));
  const all = layout.fields.flatMap((f) => f.roof.polygon.map(toW));
  if (!all.length) return null;
  const mg = 3 * prj.ppm;
  let x0 = Math.min(...all.map((q) => q[0])) - mg, x1 = Math.max(...all.map((q) => q[0])) + mg;
  let y0 = Math.min(...all.map((q) => q[1])) - mg, y1 = Math.max(...all.map((q) => q[1])) + mg;
  // adatta il riquadro al rapporto della pagina
  const cx = (x0 + x1) / 2, cy = (y0 + y1) / 2;
  let w = x1 - x0, h = y1 - y0;
  if (w / h > aspect) h = w / aspect; else w = h * aspect;
  x0 = cx - w / 2; y0 = cy - h / 2;
  const W = maxPx, H = Math.round(maxPx / aspect), k = W / w;
  const cv = document.createElement("canvas");
  cv.width = W; cv.height = H;
  const ctx = cv.getContext("2d");
  ctx.fillStyle = "#1e293b"; ctx.fillRect(0, 0, W, H);
  const world = () => ctx.setTransform(k, 0, 0, k, -x0 * k, -y0 * k);

  const bg = p.background, T = bg?.file_id && bg.visible !== false ? georefTransform(bg, toW) : null;
  let source = "satellite";
  if (T && opts.bg !== "satellite") {
    try {
      const im = await loadImg(`${API}/api/files/${bg.file_id}`);
      world(); ctx.translate(T.x, T.y); ctx.rotate(T.rot); ctx.scale(T.s, T.s); ctx.drawImage(im, 0, 0);
      source = "foto";
    } catch { source = "satellite"; }
  }
  if (source === "satellite") {
    const radius = Math.ceil(Math.max(Math.abs(x0), Math.abs(x0 + w), Math.abs(y0), Math.abs(y0 + h)) / 256) + 1;
    const tiles = prj.tiles(radius).filter((t) => t.x + 256 >= x0 && t.x <= x0 + w && t.y + 256 >= y0 && t.y <= y0 + h);
    const imgs = await Promise.all(tiles.map((t) => loadImg(t.url).then((im) => [t, im]).catch(() => null)));
    world();
    imgs.filter(Boolean).forEach(([t, im]) => ctx.drawImage(im, t.x, t.y, 257, 257));
  }

  world();
  const lw = 1 / k;
  const path = (pts, close = true) => { ctx.beginPath(); pts.forEach((q, i) => (i ? ctx.lineTo(q[0], q[1]) : ctx.moveTo(q[0], q[1]))); if (close) ctx.closePath(); };
  for (const f of layout.fields) {
    path(f.roof.polygon.map(toW));
    ctx.fillStyle = "rgba(56,189,248,0.10)"; ctx.fill();
    ctx.strokeStyle = COLORS.roof; ctx.lineWidth = 3 * lw; ctx.stroke();
    for (const c of f.active) {
      path(rectToLL(c.rect, f.frame).map(toW));
      ctx.fillStyle = "rgba(30,58,138,0.82)"; ctx.fill();
      ctx.strokeStyle = "#e0f2fe"; ctx.lineWidth = 1.2 * lw; ctx.stroke();
    }
    const fr = f.frame;
    for (const o of f.roof.obstacles || []) {
      const sh = obstacleShape(o, fr);
      path(sh.pts.map((q) => toW(fr.toLL(q))), sh.closed);
      if (sh.closed) { ctx.fillStyle = "rgba(239,68,68,0.35)"; ctx.fill(); }
      ctx.strokeStyle = COLORS.obstacle; ctx.lineWidth = 2.5 * lw; ctx.stroke();
    }
  }
  for (const r of drawing?.rails || []) {
    path(r.line.map(toW), false);
    ctx.strokeStyle = r.ok ? COLORS.rail : "#dc2626"; ctx.lineWidth = 3.5 * lw; ctx.stroke();
  }
  for (const hk of drawing?.hooks || []) {
    const q = toW(hk.pt);
    ctx.beginPath(); ctx.arc(q[0], q[1], 0.08 * prj.ppm, 0, 2 * Math.PI);
    ctx.fillStyle = hk.ok ? COLORS.hook : "#dc2626"; ctx.fill(); ctx.strokeStyle = "#fff"; ctx.lineWidth = 1.5 * lw; ctx.stroke();
  }
  for (const b of drawing?.ballast || []) {
    const q = toW(b.pt);
    ctx.beginPath(); ctx.arc(q[0], q[1], 0.16 * prj.ppm, 0, 2 * Math.PI);
    ctx.fillStyle = COLORS.ballast; ctx.fill(); ctx.strokeStyle = "#0f172a"; ctx.lineWidth = 1.5 * lw; ctx.stroke();
  }
  for (const sc of opts.sections || []) drawSectionLine(ctx, sc, toW, W);
  ctx.__q = [];
  if (opts.dims) drawDims(ctx, layout, toW, k, prj.ppm, W);
  if (opts.groupDims) drawGroupDims(ctx, layout, toW, W, p.roof_type === "piano");
  flushLabels(ctx);
  return { dataUrl: cv.toDataURL("image/jpeg", 0.9), widthM: w / prj.ppm, heightM: h / prj.ppm, source };
}

const f2 = (v) => v.toFixed(2).replace(".", ",");

// con ctx.__q attiva le scritte vengono raccolte e posizionate alla fine senza sovrapposizioni
function label(ctx, txt, x, y, px, color = "#ffffff", dir = [1, 0]) {
  if (ctx.__q) { ctx.__q.push({ txt, x, y, px, color, dir }); return; }
  drawText(ctx, txt, x, y, px, color);
}

function flushLabels(ctx) {
  const q = ctx.__q;
  ctx.__q = null;
  ctx.save(); ctx.setTransform(1, 0, 0, 1, 0, 0);
  const items = q.map((it) => { ctx.font = tr("600 {0}px Helvetica, Arial, sans-serif", [it.px]); return { ...it, w: ctx.measureText(it.txt).width + it.px * 0.4, h: it.px * 1.15 }; });
  ctx.restore();
  for (const it of placeLabels(items, ctx.__fixed || [])) {
    if (it.leader) {
      ctx.save(); ctx.setTransform(1, 0, 0, 1, 0, 0);
      ctx.strokeStyle = "rgba(255,255,255,0.85)"; ctx.lineWidth = Math.max(1.5, it.px * 0.06);
      ctx.beginPath(); ctx.moveTo(it.ax, it.ay); ctx.lineTo(it.x, it.y); ctx.stroke(); ctx.restore();
    }
    drawText(ctx, it.txt, it.x, it.y, it.px, it.color);
  }
}

function drawText(ctx, txt, x, y, px, color) {
  ctx.save(); ctx.setTransform(1, 0, 0, 1, 0, 0);
  ctx.font = tr("600 {0}px Helvetica, Arial, sans-serif", [px]); ctx.textAlign = "center"; ctx.textBaseline = "middle";
  ctx.lineJoin = "round"; ctx.lineWidth = px * 0.28; ctx.strokeStyle = "rgba(15,23,42,0.92)"; ctx.strokeText(txt, x, y);
  ctx.fillStyle = color; ctx.fillText(txt, x, y); ctx.restore();
}

function segFoot(p, a, b) {
  const dx = b[0] - a[0], dy = b[1] - a[1], L2 = dx * dx + dy * dy || 1;
  const t = Math.max(0, Math.min(1, ((p[0] - a[0]) * dx + (p[1] - a[1]) * dy) / L2));
  return [a[0] + t * dx, a[1] + t * dy];
}

// quote: lunghezza di ogni lato delle falde (in pianta) e distanza minima del campo moduli da ogni bordo
function drawDims(ctx, layout, toW, k, ppm, W) {
  const px = Math.round(W / 92);
  const scr = (q) => { const m = ctx.getTransform(); return [m.a * q[0] + m.e, m.d * q[1] + m.f]; };
  const seen = new Set();
  for (const f of layout.fields) {
    const uv = f.polyUV, n = uv.length;
    const cu = uv.reduce((t, q) => t + q[0], 0) / n, cv = uv.reduce((t, q) => t + q[1], 0) / n;
    for (let i = 0; i < n; i++) {
      const a = uv[i], b = uv[(i + 1) % n];
      const key = [a, b].map((q) => f.frame.toLL(q).map((x) => x.toFixed(6)).join(",")).sort().join("|");
      if (seen.has(key)) continue;
      seen.add(key);
      const L = Math.hypot(b[0] - a[0], b[1] - a[1]);
      if (L < 0.3) continue;
      const mx = (a[0] + b[0]) / 2, my = (a[1] + b[1]) / 2;
      let nx = -(b[1] - a[1]) / L, ny = (b[0] - a[0]) / L;
      if ((cu - mx) * nx + (cv - my) * ny > 0) { nx = -nx; ny = -ny; }
      const ea = scr(toW(f.frame.toLL(a))), eb = scr(toW(f.frame.toLL(b)));
      label(ctx, `${f2(L)} m`, ...scr(toW(f.frame.toLL([mx + nx * 1.0, my + ny * 1.0]))), px, "#ffffff", [eb[0] - ea[0], eb[1] - ea[1]]);
    }
    const corners = f.active.flatMap((c) => [[c.rect[0], c.rect[1]], [c.rect[2], c.rect[1]], [c.rect[2], c.rect[3]], [c.rect[0], c.rect[3]]]);
    if (!corners.length) continue;
    for (let i = 0; i < n; i++) {
      const a = uv[i], b = uv[(i + 1) % n];
      let best = null;
      for (const c of corners) { const ft = segFoot(c, a, b), d = Math.hypot(c[0] - ft[0], c[1] - ft[1]); if (!best || d < best.d) best = { c, ft, d }; }
      if (!best || best.d < 0.01) continue;
      const A = scr(toW(f.frame.toLL(best.c))), B = scr(toW(f.frame.toLL(best.ft)));
      ctx.save(); ctx.setTransform(1, 0, 0, 1, 0, 0);
      ctx.strokeStyle = "#facc15"; ctx.lineWidth = Math.max(2, px * 0.12); ctx.setLineDash([px * 0.4, px * 0.25]);
      ctx.beginPath(); ctx.moveTo(...A); ctx.lineTo(...B); ctx.stroke(); ctx.setLineDash([]);
      for (const q of [A, B]) { ctx.beginPath(); ctx.arc(q[0], q[1], px * 0.16, 0, 2 * Math.PI); ctx.fillStyle = "#facc15"; ctx.fill(); }
      ctx.restore();
      label(ctx, f2(best.d), (A[0] + B[0]) / 2, (A[1] + B[1]) / 2, px, "#fde047", [B[0] - A[0], B[1] - A[1]]);
    }
  }
}

const scrOf = (ctx) => { const m = ctx.getTransform(); return (q) => [m.a * q[0] + m.e, m.d * q[1] + m.f]; };

function dimLine(ctx, A, B, px, color) {
  ctx.save(); ctx.setTransform(1, 0, 0, 1, 0, 0);
  ctx.strokeStyle = color; ctx.lineWidth = Math.max(2, px * 0.12);
  ctx.beginPath(); ctx.moveTo(...A); ctx.lineTo(...B); ctx.stroke();
  const L = Math.hypot(B[0] - A[0], B[1] - A[1]) || 1, nx = -(B[1] - A[1]) / L, ny = (B[0] - A[0]) / L, t = px * 0.45;
  for (const q of [A, B]) { ctx.beginPath(); ctx.moveTo(q[0] - nx * t, q[1] - ny * t); ctx.lineTo(q[0] + nx * t, q[1] + ny * t); ctx.stroke(); }
  ctx.restore();
}

// ingombro di ogni gruppo di moduli: larghezza lungo gronda, lunghezza vera lungo falda, righe × colonne;
// tetti piani: lunghezza di ogni fila e n° moduli, più lo spazio libero tra file consecutive
function drawGroupDims(ctx, layout, toW, W, flat) {
  const px = Math.round(W / 92), scr = scrOf(ctx), C = "#22d3ee", off = 0.45;
  for (const f of layout.fields) {
    const P = (u, v) => scr(toW(f.frame.toLL([u, v])));
    for (const g of moduleGroups(f, flat)) {
      const a = P(g.u0, g.v0 - (flat ? 0.15 : off)), b = P(g.u1, g.v0 - (flat ? 0.15 : off));
      dimLine(ctx, a, b, px, C);
      if (g.flatRow) { label(ctx, tr("{0} m · {1} mod.", [f2(g.width), g.n]), (a[0] + b[0]) / 2, (a[1] + b[1]) / 2, px, "#a5f3fc", [b[0] - a[0], b[1] - a[1]]); continue; }
      label(ctx, `${f2(g.width)} m`, (a[0] + b[0]) / 2, (a[1] + b[1]) / 2, px, "#a5f3fc", [b[0] - a[0], b[1] - a[1]]);
      const c = P(g.u0 - off, g.v0), d = P(g.u0 - off, g.v1);
      dimLine(ctx, c, d, px, C);
      label(ctx, `${f2(g.length)} m`, (c[0] + d[0]) / 2, (c[1] + d[1]) / 2, px, "#a5f3fc", [d[0] - c[0], d[1] - c[1]]);
      const m = P((g.u0 + g.u1) / 2, (g.v0 + g.v1) / 2);
      label(ctx, `${g.rows} × ${g.cols}${g.full ? "" : "*"}`, m[0], m[1], Math.round(px * 1.15), "#ffffff", [b[0] - a[0], b[1] - a[1]]);
    }
    if (flat) for (const gp of rowGaps(f)) {
      const a = P(gp.u, gp.va), b = P(gp.u, gp.vb);
      dimLine(ctx, a, b, px, "#f472b6");
      label(ctx, f2(gp.gap), (a[0] + b[0]) / 2, (a[1] + b[1]) / 2, px, "#fbcfe8", [b[0] - a[0], b[1] - a[1]]);
    }
  }
}

// linea di sezione A–A sulla falda
function drawSectionLine(ctx, sc, toW, W) {
  const span = sectionSpan(sc.f, sc.u);
  if (!span) return;
  const px = Math.round(W / 80), scr = scrOf(ctx);
  const A = scr(toW(sc.f.frame.toLL([sc.u, span[0] - 0.6]))), B = scr(toW(sc.f.frame.toLL([sc.u, span[1] + 0.6])));
  ctx.save(); ctx.setTransform(1, 0, 0, 1, 0, 0);
  ctx.strokeStyle = "#ef4444"; ctx.lineWidth = px * 0.18; ctx.setLineDash([px * 0.9, px * 0.3, px * 0.2, px * 0.3]);
  ctx.beginPath(); ctx.moveTo(...A); ctx.lineTo(...B); ctx.stroke(); ctx.setLineDash([]);
  for (const q of [A, B]) { ctx.beginPath(); ctx.arc(q[0], q[1], px * 0.75, 0, 2 * Math.PI); ctx.fillStyle = "#ef4444"; ctx.fill(); }
  ctx.restore();
  for (const q of [A, B]) {
    drawText(ctx, sc.letter, q[0], q[1], px, "#ffffff");
    ctx.__fixed = [...(ctx.__fixed || []), { x: q[0], y: q[1], w: px * 1.6, h: px * 1.6 }];
  }
}
